import { useState, useEffect } from 'react';
import { type Request } from '../../../shared/types';
import { getCurrentSessionToken, fetchActiveProducersCount } from '../../auth';
import {
  fetchOperations,
  fetchStats,
  type DashboardStats
} from '../services/operationService';

export function useDashboardData(filterType: string) {
  const [requests, setRequests] = useState<{ today: Request[]; deferred: Request[] }>({ today: [], deferred: [] });
  const [activeProducers, setActiveProducers] = useState<number | string>('...');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const mapOperationToRequest = (op: any): Request => ({
    id: op.id,
    producer: op.producer_name || 'Desconocido',
    producerId: op.producer_number || 'S/N',
    type: op.type === 'echeque' ? 'e-cheque' : 'transferencia',
    amount: op.amount,
    date: new Date(
      op.request_date.replace(' ', 'T').includes('Z') 
        ? op.request_date.replace(' ', 'T') 
        : `${op.request_date.replace(' ', 'T')}Z`
    ).toLocaleString('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(',', ''),
    status: 'pending'
  });

  const loadDashboardData = async (typeOverride?: any) => {
    try {
      setIsLoading(true);
      const token = await getCurrentSessionToken();
      if (!token) return;

      const activeFilter = typeof typeOverride === 'string' ? typeOverride : filterType;

      try {
        const count = await fetchActiveProducersCount(token);
        setActiveProducers(count);
      } catch (err) {
        console.warn("Failed to load active producers count:", err);
      }

      const backendType = activeFilter === 'e-cheque' 
        ? 'echeque' 
        : activeFilter === 'transferencia' 
          ? 'transferencia' 
          : undefined;

      try {
        const s = await fetchStats(token, backendType);
        setStats(s);
      } catch (err) {
        console.warn("Failed to load dashboard stats:", err);
      }

      const todayData = await fetchOperations(token, {
        status: 'pendiente',
        group: 'del_dia',
        type: backendType
      });

      const deferredData = await fetchOperations(token, {
        status: 'pendiente',
        group: 'diferidas',
        type: backendType
      });

      setRequests({
        today: todayData.items.map(mapOperationToRequest),
        deferred: deferredData.items.map(mapOperationToRequest)
      });
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData(filterType);
  }, [filterType]);

  return {
    requests,
    activeProducers,
    stats,
    isLoading,
    reload: loadDashboardData
  };
}
